import { useEffect, useRef, useState } from 'react'
import * as d3 from 'd3'
import domToImage from 'dom-to-image-more'
import '@itsce-geospatial/d3-equal-earth-map-component'
import './worldmapTooltip.css'
import { COLOR_MAP } from '../../../common/utils/theme'
import '../common/fullScreen.css'
import useCloseOnOutsideClick from '../../../common/hooks/useCloseOnOutsideClick'
import {
  isValidDataForColorScale,
  parseColorToRGB,
} from '../../../common/utils/helper'
import {
  BinTypes,
  mapTypes,
  Breakpoint,
  LegendType,
  ScaleTypes,
  chartTypes,
} from '../../../common/utils/enum'
import ContinuousMapLegend from '../../../common/components/WorldMap/ContinuousMapLegend'
import MapLegend from '../../../common/components/WorldMap/MapLegend'
import StandardDeviationLegend from '../../../common/components/WorldMap/StandardDeviationLegend'
import useDeviceIdentification from '../../../common/hooks/useDeviceIdentification'
import { CATEGORICAL_UNITMEASURE } from '../../../common/utils/constants'

const WorldMap = ({
  getTooltipContent,
  features,
  countryCentroids,
  getColorScale,
  selectedCountries,
  indicatorCode,
  scaleType,
  indicatorName,
  badgesDiv,
  subtitle,
  legendRange,
  min,
  mid,
  max,
  legendType,
  binCount,
  unitMeasure,
  decimals,
  missingDataColor,
  binType,
  legendValuesForStd,
  boundaryType,
}) => {
  let width = 800
  let height = 450
  const isMobile = useDeviceIdentification(Breakpoint.Mobile)
  const worldMapContainerRef = useRef(null)
  const worldmapSvgRef = useRef(null)
  const [mapHeight, setMapHeight] = useState(height)
  const [showMenu, setShowMenu] = useState(false)
  const [isFullScreenMode, setIsFullScreenMode] = useState(false)
  const worldmapDownloadMenuRef = useRef(null)
  const [geojson, setGeojson] = useState(null)
  const [mapCenter, setMapCenter] = useState([15, 11])
  const [mapZoom, setMapZoom] = useState({
    initial: isMobile ? 0.4 : 1,
    min: isMobile ? 0.4 : 1,
    max: 15,
  })
  const [geoJsonKey, setGeoJsonKey] = useState(0)

  useCloseOnOutsideClick(worldmapDownloadMenuRef, () => setShowMenu(false))

  useEffect(() => {
    const handleResize = () => {
      const containerWidth = worldMapContainerRef?.current?.offsetWidth
      if (containerWidth) {
        setMapHeight(containerWidth / 2)
      }
    }
    window.addEventListener('resize', handleResize)
    handleResize()
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  useEffect(() => {
    if (features?.length > 0) {
      const geojsonData = prepareGeojson(
        features,
        getColorScale,
        selectedCountries,
        missingDataColor,
        binType,
        legendRange,
        scaleType
      )
      setGeojson(geojsonData)
      const currentGeojsonString = JSON.stringify(geojsonData)
      if (geojson !== currentGeojsonString) {
        setGeoJsonKey((prev) => prev + 1)
      }
    }
  }, [features, mapHeight]) // Note: selectedCountries dependency removed it since it was causing this issue for custom report

  useEffect(() => {
    const mapElement = worldmapSvgRef?.current
    if (!mapElement || !worldMapContainerRef?.current) return

    mapElement.countryNamesEnabled =
      boundaryType === mapTypes.Country || boundaryType === mapTypes.IncomeGroup

    mapElement.showCountries =
      boundaryType === mapTypes.Country || boundaryType === mapTypes.IncomeGroup

    mapElement.showRegions = boundaryType === mapTypes.Region

    mapElement.showcontinents = boundaryType === mapTypes.Continent

    mapElement.showdisputedareas =
      boundaryType === mapTypes.Country || boundaryType === mapTypes.IncomeGroup

    const handleMouseOver = (event) => {
      d3.select(`#world_map-${indicatorCode} .worldmap-tooltip`).remove()

      const { detail } = event
      const feature = detail?.feature
      if (feature && !feature.properties.disputed_iso3) {
        const containerRect =
          worldMapContainerRef?.current?.getBoundingClientRect()
        const x = detail?.mouseEvent?.clientX - containerRect?.left + 10
        const y = detail?.mouseEvent?.clientY - containerRect?.top + 10
        createTooltip(feature, { x, y })
      }
    }

    const handleMouseLeave = () => {
      d3.select(`#world_map-${indicatorCode} .worldmap-tooltip`).remove()
    }

    mapElement?.addEventListener('mouseOver', handleMouseOver)
    mapElement?.addEventListener('mouseLeave', handleMouseLeave)

    return () => {
      mapElement?.removeEventListener('mouseOver', handleMouseOver)
      mapElement?.removeEventListener('mouseLeave', handleMouseLeave)
    }
  }, [getTooltipContent, indicatorCode, geojson, geoJsonKey, boundaryType])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        !event.target.closest('.worldmap-tooltip') &&
        !event.target.closest(`#worldmapSvg-${indicatorCode}`)
      ) {
        d3.select(`#world_map-${indicatorCode} .worldmap-tooltip`).remove()
      }
    }
    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [indicatorCode])

  useEffect(() => {
    // Reset zoom and center when boundary type changes
    setMapCenter([15, 11])
    setMapZoom({
      initial: isMobile ? 0.4 : 1,
      min: isMobile ? 0.4 : 1,
      max: 15,
    })
    d3.select(`#world_map-${indicatorCode} .worldmap-tooltip`).remove()
    setGeoJsonKey((prev) => prev + 1)
  }, [boundaryType, isMobile, indicatorCode])

  useEffect(() => {
    if (selectedCountries?.length === 1 && boundaryType === mapTypes.Country) {
      const selectedCountryIso3 = selectedCountries?.[0]
      const countryData = countryCentroids?.country_centroids?.find(
        (country) => country?.iso3 === selectedCountryIso3
      )
      if (countryData) {
        const [longitude, latitude] = countryData?.centroids
          .replace('POINT (', '')
          .replace(')', '')
          .split(' ')
          .map(parseFloat)
        setMapCenter([longitude, latitude])
        setMapZoom({
          initial: 4,
          min: isMobile ? 0.4 : 1,
          max: 15,
        })

        const selectedFeature = features?.find(
          (feature) => feature?.properties?.ISO_A3 === selectedCountryIso3
        )
        if (selectedFeature) {
          const containerRect =
            worldMapContainerRef?.current?.getBoundingClientRect()
          const centerX = containerRect?.width / 2
          const centerY = containerRect?.height / 2
          createTooltip(selectedFeature, { x: centerX, y: centerY })
        }
      }
    } else if (boundaryType === mapTypes.Country) {
      setMapCenter([15, 11])
      setMapZoom({
        initial: isMobile ? 0.4 : 1,
        min: isMobile ? 0.4 : 1,
        max: 15,
      })
      d3.select(`#world_map-${indicatorCode} .worldmap-tooltip`).remove()
    }
  }, [
    selectedCountries,
    features,
    getTooltipContent,
    indicatorCode,
    isMobile,
    boundaryType,
  ])

  const prepareGeojson = (
    features,
    getColorScale,
    selectedCountries,
    missingDataColor,
    binType,
    legendRange,
    scaleType
  ) => {
    const noDataColor = missingDataColor
      ? missingDataColor
      : COLOR_MAP.not_available

    const styledFeatures = features.map((feature) => {
      let color

      if (feature.properties.disputed_iso3 && selectedCountries) {
        const [code1, code2] = feature.properties.disputed_iso3.split('-')

        const country1 = features.find((f) => f.properties.ISO_A3 === code1)
        const country2 = features.find((f) => f.properties.ISO_A3 === code2)

        const value1 = country1 ? country1.properties.value : null
        const value2 = country2 ? country2.properties.value : null

        const color1 =
          value1 !== undefined && value1 !== null && getColorScale
            ? getColorScale(value1)
            : noDataColor
        const color2 =
          value2 !== undefined && value2 !== null && getColorScale
            ? getColorScale(value2)
            : noDataColor

        const rgb1 = parseColorToRGB(color1, noDataColor)
        const rgb2 = parseColorToRGB(color2, noDataColor)

        const averageRGB = {
          r: Math.round((rgb1.r + rgb2.r) / 2),
          g: Math.round((rgb1.g + rgb2.g) / 2),
          b: Math.round((rgb1.b + rgb2.b) / 2),
        }

        color = `rgb(${averageRGB.r}, ${averageRGB.g}, ${averageRGB.b})`
      } else {
        const value = feature.properties.value
        if (!getColorScale) {
          color = noDataColor
        } else if (
          scaleType === ScaleTypes.Categorical ||
          unitMeasure === CATEGORICAL_UNITMEASURE
        ) {
          if (isValidDataForColorScale(value) && getColorScale) {
            color = getColorScale(value)
          } else {
            color = noDataColor
          }
        } else {
          if (isValidDataForColorScale(value)) {
            if (binType === BinTypes.StandardDeviation) {
              color = getColorScale(value, legendRange)
            } else {
              color = getColorScale(value)
            }
          } else {
            color = noDataColor
          }
        }
      }

      return {
        ...feature,
        properties: {
          ...feature.properties,
          color,
          fillOpacity: 1,
        },
      }
    })
    return JSON.stringify({
      type: 'FeatureCollection',
      features: styledFeatures,
    })
  }

  const createTooltip = (feature, position) => {
    d3.select(`#world_map-${indicatorCode} .worldmap-tooltip`).remove()

    const tooltipContent = getTooltipContent(feature)
    const tooltip = d3
      .select(`#world_map-${indicatorCode}`)
      .append('div')
      .attr('class', 'worldmap-tooltip')
      .html(tooltipContent)
      .style('position', 'absolute')
      .style('z-index', 10700)
      .style('display', 'block')
      .style('margin', '0')
      .style('font-size', '12px')
      .style('word-wrap', 'break-word')
      .style('background-color', COLOR_MAP.map.tooltip.background)
      .style('border', `solid ${COLOR_MAP.map.tooltip.border} 1px`)
      .style('border-radius', '2px')
      .style('padding', '5px')
      .style('width', 'auto')
      .style('visibility', 'visible')
      .style('pointer-events', 'none')

    const { x, y } = position
    tooltip.style('left', `${x}px`).style('top', `${y}px`)

    const containerRect = worldMapContainerRef?.current?.getBoundingClientRect()
    const tooltipRect = tooltip?.node()?.getBoundingClientRect()
    if (x + tooltipRect?.width > containerRect?.width) {
      tooltip?.style(
        'left',
        `${containerRect?.width - tooltipRect?.width - 10}px`
      )
    }
    if (y + tooltipRect?.height > containerRect?.height) {
      tooltip?.style(
        'top',
        `${containerRect?.height - tooltipRect?.height - 10}px`
      )
    }
  }

  const exportAsImage = (fileName, type) => {
    const node = document.getElementById('worldmap_container')

    if (type === 'png') {
      domToImage
        .toPng(node, { filter: excludeElements, bgcolor: '#ffffff' })
        .then((dataUrl) => {
          const link = document.createElement('a')
          link.download = fileName
          link.href = dataUrl
          link.click()
        })
        .catch((err) => {
          console.log(err)
        })
    } else if (type === 'jpeg') {
      domToImage
        .toJpeg(node, { filter: excludeElements, bgcolor: '#ffffff' })
        .then((dataUrl) => {
          const link = document.createElement('a')
          link.download = fileName
          link.href = dataUrl
          link.click()
        })
        .catch((err) => {
          console.log(err)
        })
    } else if (type === 'svg') {
      domToImage
        .toSvg(node, { filter: excludeElements, bgcolor: '#ffffff' })
        .then((dataUrl) => {
          const link = document.createElement('a')
          link.download = fileName
          link.href = dataUrl
          link.click()
        })
        .catch((err) => {
          console.log(err)
        })
    }
  }

  const excludeElements = (node) => {
    const exclusionClasses = ['download-exclude']
    return !exclusionClasses.some((classname) =>
      node.classList?.contains(classname)
    )
  }

  const handleFullscreen = () => {
    if (!document.fullscreenElement) {
      enterFullscreen()
    } else {
      exitFullscreen()
    }
  }

   useEffect(() => {
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement) {
        setIsFullScreenMode(false)
      }
    }
    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange)
  }, [])

  const enterFullscreen = () => {
    const elem = document.getElementById('worldmap_container')
    if (elem.requestFullscreen) {
      elem.requestFullscreen()
    } else if (elem.mozRequestFullScreen) {
      // Firefox
      elem.mozRequestFullScreen()
    } else if (elem.webkitRequestFullscreen) {
      // Chrome, Safari, and Opera
      elem.webkitRequestFullscreen()
    }
     setIsFullScreenMode(true)
  }

  const exitFullscreen = () => {
    if (document.exitFullscreen) {
      document.exitFullscreen()
    } else if (document.mozCancelFullScreen) {
      // Firefox
      document.mozCancelFullScreen()
    } else if (document.webkitExitFullscreen) {
      // Chrome, Safari, and Opera
      document.webkitExitFullscreen()
    }
  }

  const handlePrint = () => {
    const element = document.getElementById('worldmap_container')
    if (!element) {
      console.error('Element not found for printing')
      return
    }

    domToImage
      .toPng(element, { filter: excludeElements, bgcolor: '#ffffff' })
      .then((dataUrl) => {
        const iframe = document.createElement('iframe')
        iframe.style.position = 'absolute'
        iframe.style.top = '-10000px'
        iframe.style.left = '-10000px'
        document.body.appendChild(iframe)

        const iframeDoc =
          iframe.contentDocument || iframe.contentWindow.document
        iframeDoc.open()
        iframeDoc.write(`
        <html>
          <body>
            <img src="${dataUrl}" style="width: 100%; height: auto;" />
            <script>
              window.onload = function() {
                window.print();
              };
            </script>
          </body>
        </html>
      `)
        iframeDoc.close()

        iframe.onload = () => {
          iframe.contentWindow.onafterprint = () => {
            document.body.removeChild(iframe)
          }
        }
      })
      .catch((error) => {
        console.error('Error generating image for print:', error)
      })
  }

  return (
    <div id="worldmap_container">
      <div
        className="tui_comparison_indicator_container tui_chart_dropdown"
        id="worldmap_title_section"
      >
        <div className="tui_comparison_title_section">
            <div className="tui_heading_h2_sm">
              <h2>{indicatorName}</h2>
            </div>
          <p className="tui__text_base">{subtitle}</p>

          <div className="tui__badge_container">
            <div className="tui_badge_group">{badgesDiv}</div>
          </div>
        </div>

          <div
            className="button-container download-exclude"
            ref={worldmapDownloadMenuRef}
          >
            <div
              className="download-icon"
              onClick={() => setShowMenu((prevState) => !prevState)}
              role="button"
              tabIndex="0"
              aria-haspopup="true"
              aria-expanded={showMenu ? 'true' : 'false'}
              id="mapDownloadMenu"
            >
              <span className="lp lp-bars"></span>
            </div>
            {/* {showMenu && ( */}
            <div
              className={`download-options download-exclude ${
                showMenu ? 'show' : 'hide'
              }`}
              aria-labelledby="mapDownloadMenu"
            >
              <ul>
                <li
                  onClick={() => {
                    handleFullscreen()
                    setShowMenu(false)
                  }}
                >
                  {isFullScreenMode
                    ? 'Exit from full screen'
                    : 'View in full screen'}
                </li>
                <li
                  onClick={() => {
                    const fileName = `${indicatorCode}_worldmap.png`
                    exportAsImage(fileName, 'png')
                    setShowMenu(false)
                  }}
                >
                  Download PNG image
                </li>
                <li
                  onClick={() => {
                    const fileName = `${indicatorCode}_worldmap.jpg`
                    exportAsImage(fileName, 'jpeg')
                    setShowMenu(false)
                  }}
                >
                  Download JPEG image
                </li>
                <li
                  onClick={() => {
                    const fileName = `${indicatorCode}_worldmap.svg`
                    exportAsImage(fileName, 'svg')
                    setShowMenu(false)
                  }}
                >
                  Download SVG
                </li>
                <li
                  onClick={() => {
                    handlePrint()
                    setShowMenu(false)
                  }}
                >
                  Print
                </li>
              </ul>
            </div>
            {/* )} */}
          </div>
      </div>

      <div className="tui__chart_content">
        {/* <div className="zoom-button-container download-exclude">
          <div className="zoom-in-icon" id="zoomInBtn">
            <span className="lp lp-plus"></span>
          </div>
          <div className="zoom-out-icon" id="zoomOutBtn">
            <span className="lp lp-minus"></span>
          </div>
        </div> */}
        <div className="chart-container">
          <div
            id={`world_map-${indicatorCode}`}
            ref={worldMapContainerRef}
            style={{
              background: 'white',
              height: mapHeight,
            }}
          >
            {geojson && features?.length > 0 ? (
              <d3-map
                id={`worldmapSvg-${indicatorCode}`}
                key={`worldmapsvg-key-${indicatorCode}-${geoJsonKey}`}
                ref={worldmapSvgRef}
                className="map"
                geojson={geojson}
                opacity="1"
                foreground={COLOR_MAP.map.foreground}
                borderColor={COLOR_MAP.map.border}
                labelColor={COLOR_MAP.map.label}
                pointVisibilityThreshold="2"
                countryNamesEnabled="true"
                center={JSON.stringify(mapCenter)}
                zoom={JSON.stringify(mapZoom)}
              />
            ) : null}
          </div>
        </div>
      </div>
      {scaleType === ScaleTypes.Categorical ||
      unitMeasure === CATEGORICAL_UNITMEASURE ||
      legendType === LegendType.Binned ? (
        <>
          {legendValuesForStd ? (
            <StandardDeviationLegend
              legendRange={legendRange}
              colors={
                scaleType === ScaleTypes.Categorical
                  ? COLOR_MAP['categorical']
                  : unitMeasure === CATEGORICAL_UNITMEASURE
                    ? COLOR_MAP['category']
                    : COLOR_MAP[`${scaleType.replace(' ', '')}_${binCount}`]
              }
              scaleType={scaleType}
              divBinnedMidValue={
                (scaleType === ScaleTypes.Diverging ||
                  scaleType === ScaleTypes.DivergingReverse ||
                  scaleType === ScaleTypes.Diverging_Reverse) &&
                legendType === LegendType.Binned
                  ? mid
                  : undefined
              }
              unitMeasure={unitMeasure}
              decimals={decimals}
              legendValuesForStd={legendValuesForStd}
              noDataColor={
                missingDataColor ? missingDataColor : COLOR_MAP.not_available
              }
            />
          ) : (
            <MapLegend
              legendRange={legendRange}
              colors={
                scaleType === ScaleTypes.Categorical ||
                unitMeasure === CATEGORICAL_UNITMEASURE
                  ? COLOR_MAP['category']
                  : COLOR_MAP[`${scaleType.replace(' ', '')}_${binCount}`]
              }
              scaleType={scaleType}
              divBinnedMidValue={
                (scaleType === ScaleTypes.Diverging ||
                  scaleType === ScaleTypes.DivergingReverse ||
                  scaleType === ScaleTypes.Diverging_Reverse) &&
                legendType === LegendType.Binned
                  ? mid
                  : undefined
              }
              unitMeasure={unitMeasure}
              decimals={decimals}
              legendValuesForStd={legendValuesForStd}
              noDataColor={
                missingDataColor ? missingDataColor : COLOR_MAP.not_available
              }
            />
          )}
        </>
      ) : (
        <ContinuousMapLegend
          min={min}
          mid={mid}
          max={max}
          scaleType={scaleType}
          unitMeasure={unitMeasure}
          decimals={decimals}
          noDataColor={
            missingDataColor ? missingDataColor : COLOR_MAP.not_available
          }
        />
      )}
    </div>
  )
}
export default WorldMap
